import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  History,
  Search,
  Play,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Database,
  Calendar,
  RefreshCw,
  Building2,
  Clock,
  StopCircle,
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

const fetchJSON = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
};

const formatNum = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : (n || 0).toLocaleString?.('en-IN') || '0');

export default function HistoricSync() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // selected brand record
  const [discover, setDiscover] = useState(null); // discover response
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null); // active job status
  const pollRef = useRef(null);

  const loadBrands = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/historic-sync/brands');
      setBrands(data.brands || []);
    } catch (e) {
      toast.error(`Failed to load brands: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  // Poll status when a brand is selected
  useEffect(() => {
    if (!selected) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = async () => {
      try {
        const data = await fetchJSON(`/api/historic-sync/status/${selected.brand_id}`);
        setStatus(data);
        // Refresh brand list if status changes (so cards stay in sync)
        if (data.status && data.status !== 'idle') {
          // light refresh, no spinner
          fetchJSON('/api/historic-sync/brands').then(d => setBrands(d.brands || [])).catch(() => {});
        }
        // Stop polling once we hit a terminal state (but resume can restart).
        if (data.status && ['completed', 'failed', 'cancelled', 'stopped'].includes(data.status) && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        // ignore transient
      }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selected]);

  const handleDiscover = async (brand) => {
    setSelected(brand);
    setDiscover(null);
    setDiscoverLoading(true);
    try {
      const data = await fetchJSON(`/api/historic-sync/discover/${brand.brand_id}`);
      setDiscover(data);
    } catch (e) {
      toast.error(`Discover failed: ${e.message}`);
    } finally {
      setDiscoverLoading(false);
    }
  };

  const handleStart = async (resume = false) => {
    if (!selected) return;
    setStarting(true);
    try {
      const data = await fetchJSON(`/api/historic-sync/start/${selected.brand_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resume ? { resume: true } : { wipe: true }),
      });
      toast.success(
        resume
          ? `Resuming ${selected.name}: ${data.fy_count} fiscal year(s) remaining`
          : `Sync started for ${selected.name}: ${data.fy_count} fiscal years queued`
      );
      setConfirmStart(false);
      // Re-arm polling — it stops on terminal states but resume restarts the job.
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setSelected({ ...selected });  // trigger the polling useEffect
    } catch (e) {
      toast.error(`Failed to ${resume ? 'resume' : 'start'}: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const handleForceStop = async () => {
    if (!selected) return;
    if (!window.confirm('Force-stop the current sync? You can Resume from the last completed fiscal year afterwards.')) return;
    try {
      await fetchJSON(`/api/historic-sync/force-stop/${selected.brand_id}`, { method: 'POST' });
      toast.info('Job force-stopped. Click Resume to continue from the last completed fiscal year.');
    } catch (e) {
      toast.error(`Force-stop failed: ${e.message}`);
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    try {
      await fetchJSON(`/api/historic-sync/cancel/${selected.brand_id}`, { method: 'POST' });
      toast.info('Cancellation requested. The current fiscal year will finish, then job will stop.');
    } catch (e) {
      toast.error(`Cancel failed: ${e.message}`);
    }
  };

  // Compute overall progress
  const overallPct = (() => {
    if (!status || !status.total_fy) return 0;
    return Math.round((status.fy_completed || 0) / status.total_fy * 100);
  })();

  const fyEntries = status?.fy_progress
    ? Object.entries(status.fy_progress).sort((a, b) => (b[0] > a[0] ? 1 : -1))
    : [];

  const isRunning = status && ['starting', 'wiping', 'syncing'].includes(status.status);

  return (
    <div className="space-y-6" data-testid="historic-sync-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="historic-sync-title">
            <History className="h-8 w-8 text-orange-500" />
            Historic Data Sync
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Pull a brand's complete history from Data Bridge — year by year — with live progress.
            Use this for first-time setup or to repair drifted data. Daily incremental syncs run automatically at 1:00 AM IST.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadBrands}
          disabled={loading}
          data-testid="refresh-brands-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((b) => (
            <Card key={b.brand_id} className="border-l-4 border-l-orange-400" data-testid={`brand-card-${b.brand_id}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4 text-orange-500" />
                  {b.name}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">ID: {b.brand_id}</Badge>
                  <Badge variant="secondary" className="text-xs">prefix: {b.prefix}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Database className="h-3.5 w-3.5" />
                    Local transactions
                  </div>
                  <div className="text-2xl font-semibold mt-1" data-testid={`local-txns-${b.brand_id}`}>
                    {formatNum(b.local_transactions)}
                  </div>
                </div>
                {b.job_status && (
                  <div className="text-xs space-y-1 p-2 rounded bg-muted/50">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">Last job:</span>
                      <Badge variant={b.job_status === 'completed' ? 'default' : b.job_status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                        {b.job_status}
                      </Badge>
                      {b.job_stalled && (
                        <Badge className="bg-orange-600 text-white text-[10px]" data-testid={`stalled-badge-${b.brand_id}`}>stalled</Badge>
                      )}
                      {b.job_resumable && !b.job_stalled && (
                        <Badge className="bg-amber-500 text-white text-[10px]" data-testid={`resumable-badge-${b.brand_id}`}>resumable</Badge>
                      )}
                    </div>
                    {b.job_current_step && <div className="text-muted-foreground truncate">{b.job_current_step}</div>}
                    {b.job_total_fy != null && (
                      <div className="text-muted-foreground">
                        {b.job_fy_completed || 0}/{b.job_total_fy} fiscal years
                      </div>
                    )}
                  </div>
                )}
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => handleDiscover(b)}
                  data-testid={`open-historic-sync-${b.brand_id}`}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Sync Historic Data
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setDiscover(null); setStatus(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="historic-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-orange-500" />
              {selected?.name} — Historic Sync
            </DialogTitle>
            <DialogDescription>
              Discover the brand's full date range from Data Bridge and pull year-by-year.
            </DialogDescription>
          </DialogHeader>

          {/* Discover panel */}
          {discoverLoading && (
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Discovering date range from Data Bridge…</span>
            </div>
          )}

          {discover && !discoverLoading && (
            <div className="space-y-4">
              <Card className="bg-orange-50 dark:bg-orange-950/20 border-orange-200">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Earliest
                      </div>
                      <div className="font-semibold mt-1" data-testid="earliest-date">{discover.earliest_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Latest
                      </div>
                      <div className="font-semibold mt-1" data-testid="latest-date">{discover.latest_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Upstream txns</div>
                      <div className="font-semibold mt-1" data-testid="upstream-total">{formatNum(discover.total_upstream)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Fiscal years</div>
                      <div className="font-semibold mt-1" data-testid="fy-count">{discover.fy_count}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Wipe warning + start button — only when no job is active OR resumable */}
              {!isRunning && !status?.resumable && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-300 text-sm space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <div className="font-semibold text-red-700 dark:text-red-300">This will WIPE all local transactions for this brand</div>
                      <div className="text-red-700/80 dark:text-red-300/80">
                        Currently {formatNum(discover.local_transactions)} local transactions will be deleted, then re-pulled fiscal year by fiscal year ({discover.fy_count} years from {discover.earliest_date} to {discover.latest_date}).
                        Aggregated stats, dashboards, and AI insights will refresh after completion.
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setConfirmStart(true)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="start-historic-sync-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Wipe & Re-pull Historic Data
                    </Button>
                  </div>
                </div>
              )}

              {/* Resume panel — when prior job exists with incomplete FYs */}
              {!isRunning && status?.resumable && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-300 text-sm space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <div className="font-semibold text-amber-800 dark:text-amber-200">
                        Resumable: {status.incomplete_fy_count} fiscal year(s) incomplete
                      </div>
                      <div className="text-amber-800/80 dark:text-amber-200/80">
                        The previous sync ended in <span className="font-semibold">{status.status}</span>{status.stalled ? ' (stalled)' : ''}.
                        Click Resume to continue from the last completed fiscal year — already-synced years will be skipped, no wipe.
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => handleStart(true)}
                      disabled={starting}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="resume-historic-sync-btn"
                    >
                      {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                      Resume Sync ({status.incomplete_fy_count} FY left)
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmStart(true)}
                      data-testid="restart-historic-sync-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Wipe & Restart from scratch
                    </Button>
                  </div>
                </div>
              )}

              {/* Stall warning banner while running */}
              {isRunning && status?.stalled && (
                <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-950/30 border border-orange-400 text-sm">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <div className="space-y-2 flex-1">
                      <div className="font-semibold text-orange-800 dark:text-orange-200">
                        Sync appears stuck — no progress for {status.seconds_since_heartbeat ? Math.round(status.seconds_since_heartbeat) + 's' : '> 3 min'}
                      </div>
                      <div className="text-orange-800/80 dark:text-orange-200/80 text-xs">
                        The current fiscal year may be hung on a slow upstream call. Force-stop the job, then Resume to retry from the last completed FY.
                      </div>
                      <Button
                        size="sm"
                        onClick={handleForceStop}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="force-stop-historic-sync-btn"
                      >
                        <StopCircle className="h-4 w-4 mr-2" />
                        Force Stop (resumable)
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Live progress */}
              {status && status.exists && (
                <div className="space-y-3">
                  {isRunning && (
                    <div className="p-3 rounded bg-blue-50 dark:bg-blue-950/20 border border-blue-300 text-xs flex items-start gap-2" data-testid="background-info-banner">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-blue-800 dark:text-blue-200">Runs in the background — safe to close this window.</span>
                        <span className="text-blue-800/80 dark:text-blue-200/80"> The sync continues server-side. Each fiscal year auto-retries up to {3} times. Reopen this dialog anytime to see live status.</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {status.status === 'completed' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                      {status.status === 'failed' && <XCircle className="h-5 w-5 text-red-600" />}
                      {isRunning && <Loader2 className="h-5 w-5 animate-spin text-orange-500" />}
                      <span className="font-semibold capitalize">{status.status}</span>
                      {status.current_step && (
                        <span className="text-sm text-muted-foreground">— {status.current_step}</span>
                      )}
                    </div>
                    {isRunning && (
                      <Button variant="outline" size="sm" onClick={handleCancel} data-testid="cancel-historic-sync-btn">
                        <StopCircle className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                  </div>

                  <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-orange-500 transition-all duration-500"
                      style={{ width: `${overallPct}%` }}
                      data-testid="overall-progress-bar"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground flex justify-between">
                    <span>{status.fy_completed || 0} / {status.total_fy || 0} fiscal years</span>
                    <span>{overallPct}%</span>
                  </div>

                  {/* Per-FY table */}
                  {fyEntries.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-2">Fiscal Year</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-right p-2">Customers</th>
                            <th className="text-right p-2">Transactions</th>
                            <th className="text-right p-2">Upstream</th>
                            <th className="text-right p-2">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fyEntries.map(([key, fy]) => (
                            <tr key={key} className="border-t" data-testid={`fy-row-${fy.label}`}>
                              <td className="p-2 font-medium">{fy.label}</td>
                              <td className="p-2">
                                {fy.status === 'syncing' && <Badge className="bg-orange-500 text-white"><Loader2 className="h-3 w-3 mr-1 animate-spin" />syncing</Badge>}
                                {fy.status === 'retrying' && <Badge className="bg-blue-500 text-white"><Loader2 className="h-3 w-3 mr-1 animate-spin" />retry {fy.attempts || 2}</Badge>}
                                {fy.status === 'done' && <Badge className="bg-green-600 text-white">done</Badge>}
                                {fy.status === 'partial' && (
                                  <Badge className="bg-yellow-500 text-white" title="Some upstream pages were not pulled within the timeout. Click Resume to retry — already-synced rows will be skipped via dedup.">partial</Badge>
                                )}
                                {fy.status === 'stalled' && (
                                  <Badge className="bg-orange-700 text-white" title="The watchdog cancelled this FY after 10 minutes with no progress. The data is incomplete — click Resume to retry this FY only.">stalled</Badge>
                                )}
                                {fy.status === 'cancelled' && <Badge variant="secondary">cancelled</Badge>}
                              </td>
                              <td className="p-2 text-right">{formatNum(fy.customers)}</td>
                              <td className="p-2 text-right">{formatNum(fy.transactions)}</td>
                              <td className="p-2 text-right text-muted-foreground">{formatNum(fy.upstream_total)}</td>
                              <td className="p-2 text-right text-muted-foreground">
                                {fy.elapsed_sec ? `${fy.elapsed_sec}s` : (fy.status === 'syncing' ? '…' : '-')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {status.status === 'completed' && (
                    <div className="p-3 rounded bg-green-50 dark:bg-green-950/20 border border-green-300 text-sm flex items-center gap-2" data-testid="sync-completed-banner">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>Sync fully completed in {status.total_elapsed_sec}s — all {status.total_fy} fiscal years done. Dashboards will reflect the new data.</span>
                    </div>
                  )}
                  {status.status === 'completed_with_gaps' && (
                    <div className="p-3 rounded bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-400 text-sm flex items-start gap-2" data-testid="sync-gaps-banner">
                      <AlertTriangle className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <div className="font-semibold text-yellow-800 dark:text-yellow-200">
                          Sync finished with gaps — {(status.incomplete_fy_labels || []).length} fiscal year(s) didn't fully pull
                        </div>
                        <div className="text-yellow-800/80 dark:text-yellow-200/80 text-xs">
                          Auto-retries already ran. Click <span className="font-semibold">Resume Sync</span> above to re-attempt the incomplete years — typically resolves a few minutes later when upstream Data Bridge load drops. Already-synced rows are skipped via dedup.
                        </div>
                      </div>
                    </div>
                  )}
                  {status.status === 'failed' && status.error && (
                    <div className="p-3 rounded bg-red-50 dark:bg-red-950/20 border border-red-300 text-sm">
                      <div className="font-semibold text-red-700">Failed:</div>
                      <div className="text-red-600 mt-1">{status.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final confirmation dialog */}
      <Dialog open={confirmStart} onOpenChange={setConfirmStart}>
        <DialogContent className="max-w-md" data-testid="confirm-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription>
              You are about to wipe <strong>{formatNum(discover?.local_transactions)}</strong> local transactions for <strong>{selected?.name}</strong> and re-pull <strong>{discover?.fy_count}</strong> fiscal years from Data Bridge.
              <br /><br />
              This may take several minutes (or hours for large brands). Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmStart(false)} disabled={starting}>
              Cancel
            </Button>
            <Button
              onClick={() => handleStart(false)}
              disabled={starting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-wipe-btn"
            >
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Clock className="h-4 w-4 mr-2" />}
              Yes, Wipe & Sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
